import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { sessionManager } from '../utils/sessionManager';

export const getBackendUrl = () => {
  if (typeof window !== 'undefined' && (window as any).RUNTIME_CONFIG) {
    return (window as any).RUNTIME_CONFIG.REACT_APP_BACKEND_URL;
  }
  return 'https://flow888.app';
};

const getWebSocketUrl = () => {
  const baseUrl = getBackendUrl();
  // Convert HTTP/HTTPS URLs to WebSocket URLs
  return baseUrl.replace(/^https?:\/\//, 'wss://');
};

const graphqlUri = `${getBackendUrl()}/graphql`;
const wsUri = `${getWebSocketUrl()}/graphql`;

const httpLink = createHttpLink({
  uri: graphqlUri,
  credentials: 'include', // Include credentials for CORS
});

// Auth link to add authorization header
const authLink = setContext((_, { headers }) => {
  // Get the authentication token from local storage if it exists
  const token = localStorage.getItem('authToken');

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    },
  };
});

// Error handling link
const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path, extensions }) => {
        // Check for authentication errors by message content
        if (
          message.includes('Unauthorized') ||
          message.includes('Authentication required') ||
          message.includes('Token expired') ||
          message.includes('Invalid token') ||
          message.includes('session has expired') ||
          message.includes('logged in on another device') ||
          message.includes('Please log in again')
        ) {
          sessionManager.handleSessionExpiration();
          return;
        }

        // Check for authentication errors by error code
        if (extensions?.code === 'UNAUTHENTICATED') {
          sessionManager.handleSessionExpiration();
          return;
        }
      });
    }

    if (networkError) {
      // Check for 401 Unauthorized network errors
      if ('statusCode' in networkError && networkError.statusCode === 401) {
        sessionManager.handleSessionExpiration();
      }
    }
  },
);

// Create WebSocket link for subscriptions
const wsLink = new GraphQLWsLink(
  createClient({
    url: wsUri,
    connectionParams: () => {
      const token = localStorage.getItem('authToken');
      return {
        Authorization: token ? `Bearer ${token}` : '',
      };
    },
    on: {
      connected: () => {
        console.log('WebSocket connected for GraphQL subscriptions');
      },
      error: (error) => {
        console.error('WebSocket connection error:', error);
      },
      closed: () => {
        console.log('WebSocket connection closed');
      },
    },
  }),
);

// Split link to use WebSocket for subscriptions and HTTP for queries/mutations
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  from([errorLink, authLink, httpLink]),
);

// Create Apollo Client instance
export const apolloClient = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
    },
    query: {
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});

export default apolloClient;